if (!customElements.get('hero-audio-toggle')) {
  customElements.define(
    'hero-audio-toggle',
    class HeroAudioToggle extends HTMLElement {
      connectedCallback() {
        this.media = document.getElementById(this.dataset.mediaId);
        this.button = this.querySelector('button');
        if (!this.media || !this.button) return;

        this.muted = true;
        this.onClick = () => this.setMuted(!this.muted);
        this.button.addEventListener('click', this.onClick);

        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.intersectionRatio < 0.5 && !this.muted) this.setMuted(true);
          },
          { threshold: 0.5 }
        );
        this.observer.observe(this.media);

        if (this.hasAttribute('data-detect')) {
          this.detectAudio();
        } else {
          this.hidden = false;
        }
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        this.observer?.disconnect();
        this.stopDetecting();
      }

      detectAudio() {
        this.video = this.media.querySelector('video');
        if (!this.video) return;

        this.onVideoProgress = () => {
          if (this.video.readyState < HTMLMediaElement.HAVE_METADATA) return;

          const hasAudio = this.hasAudioTrack(this.video);
          if (hasAudio === undefined) return;

          this.stopDetecting();
          if (hasAudio !== false) this.hidden = false;
        };

        this.video.addEventListener('loadedmetadata', this.onVideoProgress);
        this.video.addEventListener('timeupdate', this.onVideoProgress);
        this.onVideoProgress();
      }

      stopDetecting() {
        if (!this.video || !this.onVideoProgress) return;
        this.video.removeEventListener('loadedmetadata', this.onVideoProgress);
        this.video.removeEventListener('timeupdate', this.onVideoProgress);
      }

      // true/false when known, undefined while Chromium hasn't decoded enough yet, null when the browser can't tell.
      hasAudioTrack(video) {
        if (typeof video.mozHasAudio === 'boolean') return video.mozHasAudio;
        if (video.audioTracks) return video.audioTracks.length > 0;
        if (typeof video.webkitAudioDecodedByteCount === 'number') {
          if (video.webkitAudioDecodedByteCount > 0) return true;
          return video.currentTime > 1 ? false : undefined;
        }
        return null;
      }

      setMuted(muted) {
        this.muted = muted;

        if (this.dataset.kind === 'vimeo') {
          this.setVimeoMuted(muted);
        } else {
          const video = this.media.querySelector('video');
          if (video) {
            video.muted = muted;
            if (!muted) video.play().catch(() => this.setMuted(true));
          }
        }

        this.button.setAttribute('aria-label', muted ? this.dataset.labelUnmute : this.dataset.labelMute);
        this.classList.toggle('hero-audio-toggle--unmuted', !muted);
      }

      setVimeoMuted(muted) {
        const iframe = this.media.querySelector('iframe');
        if (!iframe) return;

        // hero-helper.js swaps the src across the mobile breakpoint, which restarts the player muted.
        if (!iframe.dataset.heroAudioWatched) {
          iframe.dataset.heroAudioWatched = '';
          iframe.addEventListener('load', () => {
            if (!this.muted) this.setMuted(true);
          });
        }

        const post = (method, value) =>
          iframe.contentWindow?.postMessage(JSON.stringify({ method, value }), 'https://player.vimeo.com');

        post('setMuted', muted);
        post('setVolume', muted ? 0 : 1);
        if (!muted) post('play');
      }
    }
  );
}
